import React, { useContext, useEffect, useReducer, useState } from "react";

import openSocket from "socket.io-client";

import {
  Button,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from "@material-ui/core";

import { makeStyles } from "@material-ui/core/styles";

import { Box, Card, CardContent, Typography, Grid } from "@mui/material";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import { DeleteOutline, Edit } from "@material-ui/icons";
import PromptModal from "../../components/PromptModal";
import { toast } from "react-toastify";
import ConfirmationModal from "../../components/ConfirmationModal";
import { AuthContext } from "../../context/Auth/AuthContext";
import usePlans from "../../hooks/usePlans";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import ForbiddenPage from "../../components/ForbiddenPage";
// import { SocketContext } from "../../context/Socket/SocketContext";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(0),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
    border: "none"
  },
  customTableCell: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
}));

const reducer = (state, action) => {
  if (action.type === "LOAD_PROMPTS") {
    const prompts = action.payload;
    const newState = [...state];

    prompts.forEach((prompt) => {
      const promptIndex = newState.findIndex((p) => p.id === prompt.id);
      if (promptIndex !== -1) {
        newState[promptIndex] = prompt;
      } else {
        newState.push(prompt);
      }
    });

    return newState;
  }

  if (action.type === "UPDATE_PROMPTS") {
    const prompt = action.payload;
    const promptIndex = state.findIndex((p) => p.id === prompt.id);

    if (promptIndex !== -1) {
      const newState = [...state];
      newState[promptIndex] = prompt;
      return newState;
    } else {
      return [prompt, ...state];
    }
  }

  if (action.type === "DELETE_PROMPT") {
    const promptId = action.payload;
    const promptIndex = state.findIndex((p) => p.id === promptId);
    if (promptIndex !== -1) {
      const newState = [...state];
      newState.splice(promptIndex, 1);
      return newState;
    }
    return state;
  }

  if (action.type === "RESET") {
    return [];
  }
};

const Prompts = () => {
  const classes = useStyles();

  const [prompts, dispatch] = useReducer(reducer, []);
  const [loading, setLoading] = useState(false);

  const [promptModalOpen, setPromptModalOpen] = useState(false);
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  //   const socketManager = useContext(SocketContext);
  const { user, socket } = useContext(AuthContext);

  const { getPlanCompany } = usePlans();
  const history = useHistory();
  const companyId = user.companyId;

  useEffect(() => {
    async function fetchData() {
      const planConfigs = await getPlanCompany(undefined, companyId);
      if (!planConfigs.plan.useOpenAi) {
        toast.error("Esta empresa não possui permissão para acessar essa página! Estamos lhe redirecionando.");
        setTimeout(() => {
          history.push(`/`)
        }, 1000);
      }
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/prompt");
        dispatch({ type: "LOAD_PROMPTS", payload: data.prompts });

        setLoading(false);
      } catch (err) {
        toastError(err);
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    // const socket = socketManager.GetSocket();

    const onPromptEvent = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_PROMPTS", payload: data.prompt });
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_PROMPT", payload: data.promptId });
      }
    };

    socket.on(`company-${companyId}-prompt`, onPromptEvent);
    return () => {
      socket.off(`company-${companyId}-prompt`, onPromptEvent);
    };
  }, [socket]);

  const handleOpenPromptModal = () => {
    setPromptModalOpen(true);
    setSelectedPrompt(null);
  };

  const handleClosePromptModal = () => {
    setPromptModalOpen(false);
    setSelectedPrompt(null);
  };

  const handleEditPrompt = (prompt) => {
    setSelectedPrompt(prompt);
    setPromptModalOpen(true);
  };

  const handleCloseConfirmationModal = () => {
    setConfirmModalOpen(false);
    setSelectedPrompt(null);
  };

  const handleDeletePrompt = async (promptId) => {
    try {
      const { data } = await api.delete(`/prompt/${promptId}`);
      toast.info(i18n.t(data.message));
    } catch (err) {
      toastError(err);
    }
    setSelectedPrompt(null);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, width: "100%" }}>
      <ConfirmationModal
        title={
          selectedPrompt &&
          `${i18n.t("prompts.confirmationModal.deleteTitle")} ${selectedPrompt.name
          }?`
        }
        open={confirmModalOpen}
        onClose={handleCloseConfirmationModal}
        onConfirm={() => handleDeletePrompt(selectedPrompt.id)}
      >
        {i18n.t("prompts.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <PromptModal
        open={promptModalOpen}
        onClose={handleClosePromptModal}
        promptId={selectedPrompt?.id}
      />
      {user.profile === "user" ?
        <ForbiddenPage />
        :
        <>
          <Card elevation={0} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Box>
                  <Typography variant="h6" color="primary" gutterBottom fontWeight="bold">
                    Inteligência Artificial (Talk.AI)
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Gerencie e configure os prompts da OpenAI/Assistentes para as suas filas.
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleOpenPromptModal}
                >
                  {i18n.t("prompts.buttons.add")}
                </Button>
              </Box>
          <Paper className={classes.mainPaper} elevation={0} variant="outlined" style={{ backgroundColor: "transparent", border: "none", padding: "16px" }}>
            <Grid container spacing={2}>
              {prompts.map((prompt) => (
                <Grid item xs={12} sm={6} md={4} key={prompt.id}>
                  <Card elevation={0} sx={{ borderRadius: 2, height: "100%", border: "1px solid", borderColor: "divider" }}>
                    <CardContent>
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                        <Box>
                          <Typography variant="h6" color="primary" fontWeight="bold">
                            {prompt.name}
                          </Typography>
                        </Box>
                        <Box display="flex">
                          <IconButton size="small" onClick={() => handleEditPrompt(prompt)}>
                            <Edit color="secondary" />
                          </IconButton>
                          <IconButton size="small" onClick={() => {
                            setSelectedPrompt(prompt);
                            setConfirmModalOpen(true);
                          }}>
                            <DeleteOutline color="error" />
                          </IconButton>
                        </Box>
                      </Box>
                      
                      <Box display="flex" justifyContent="space-between">
                        <Box>
                          <Typography variant="caption" color="textSecondary" display="block">
                            {i18n.t("prompts.table.queue")}
                          </Typography>
                          <Typography variant="body2" fontWeight="bold">
                            {prompt.queue.name || '-'}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="textSecondary" display="block">
                            {i18n.t("prompts.table.max_tokens")}
                          </Typography>
                          <Typography variant="body2" fontWeight="bold">
                            {prompt.maxTokens || '-'}
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
              {loading && (
                <Grid item xs={12}>
                  <TableRowSkeleton columns={4} />
                </Grid>
              )}
            </Grid>
          </Paper>
            </CardContent>
          </Card>
        </>}
    </Box>
  );
};

export default Prompts;